import { Button } from '@auctisync/ui';
import { useFollow, type FollowKind } from '../hooks/useFollow';
import { useSession } from '../hooks/useSession';

interface FollowButtonProps {
  kind: FollowKind;
  targetId: string | null;
  /** Label shown when not following — e.g. "Follow this auction". */
  followLabel?: string;
  /** Label shown when following — e.g. "Following". */
  followingLabel?: string;
  /** What to show when there's no signed-in user (default "Sign in to follow"). */
  signedOutLabel?: string;
  className?: string;
}

/**
 * Module #23 chunk 2a — follow / unfollow toggle button.
 *
 * Hides itself when target_id is null (still loading). Renders a "sign in"
 * link when no session. Disabled + spinning while a mutation is in flight.
 */
export function FollowButton({
  kind,
  targetId,
  followLabel = kind === 'auction' ? 'Follow auction' : 'Follow seller',
  followingLabel = 'Following',
  signedOutLabel = 'Sign in to follow',
  className,
}: FollowButtonProps) {
  const { session } = useSession();
  const { ready, following, pending, toggle, error } = useFollow(kind, targetId);

  if (!targetId) return null;

  if (!session) {
    return (
      <Button asChild variant="outline" size="sm" className={className}>
        {/* `/my` is not a route — the bidder account lives at `/my/dashboard`
            and the sign-in page at `/signin`. This link had been dead since it
            was written; someone following the prompt landed on the catch-all. */}
        <a href="/signin">{signedOutLabel}</a>
      </Button>
    );
  }

  return (
    <div style={{ display: 'inline-flex', flexDirection: 'column', gap: '0.25rem' }}>
      <Button
        type="button"
        variant={following ? 'outline' : 'default'}
        size="sm"
        disabled={!ready || pending}
        onClick={() => {
          void toggle();
        }}
        className={className}
      >
        {pending ? '…' : following ? `★ ${followingLabel}` : `☆ ${followLabel}`}
      </Button>
      {error ? (
        <span style={{ fontSize: '0.75rem', color: 'var(--text-danger, #991b1b)' }}>{error}</span>
      ) : null}
    </div>
  );
}
